import React, { Component } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
// Formatted text JSON map:

export const sectionDataFunc = (isUKEnable, intl) => ({
  length: {
    Summary: {
      text: (
        <p>
          {intl.formatMessage({
            id: 'infoPresentation.length.summaryText',
            defaultMessage:
              'Your summary should feel complete without rambling. It needs room for a hook, your story, outcomes, and a closing line. Right-sized summaries improve recruiter engagement, increase profile search matches, and make your value obvious within seconds. Write within 1,600–2,200 characters to hit the sweet spot.',
          })}
        </p>
      ),
      correct: (
        <>
            <p>
            {intl.formatMessage(
                {
                id: 'infoPresentation.length.summaryCorrect1',
                defaultMessage:
                    'I build products that turn messy signals into simple decisions for customers. My focus is the space where user research, data, and design meet execution. I like hard problems that touch real people—activation that actually sticks, onboarding that respects time, and insights that lead to better choices. Colleagues know me for a calm, structured approach and a bias to ship. I’m hands-on with discovery and comfortable writing crisp specs, but I’m happiest when the team understands the “why,” not just the backlog. I keep meetings light, documents clear, and experiments small enough to learn without drama.'
                }
            )}
            </p>
            <p>
            {intl.formatMessage(
                {
                id: 'infoPresentation.length.summaryCorrect2',
                defaultMessage:
                    'Recently, I’ve partnered with engineers, designers, and analysts to streamline first-run experiences, cut dead-end clicks, and surface value earlier in the journey. I set clear success metrics, run lightweight experiments, and share outcomes openly so decisions age well. Highlights include improving day-seven retention with focused prompts, reducing support tickets by clarifying copy at key moments, and establishing a weekly insights cadence that aligned leadership and squad priorities. I enjoy translating qualitative research into quantitative tests, then bringing the story back to the team so results stick. When tradeoffs get tense, I slow the room, name the risks, and help us choose a reversible path first.'
                }
            )}
            </p>
            <p>
            {intl.formatMessage(
                {
                id: 'infoPresentation.length.summaryCorrect3',
                defaultMessage:
                    'I’m excited by products that help people learn faster, manage money wisely, stay healthy, or collaborate with less friction. I care about accessibility, responsible data use, and steady craftsmanship over loud launches. Outside of roadmaps, I mentor early-career builders, read widely, and prototype ideas for fun—tiny tools that remove paper cuts, dashboards that show what matters, and micro-experiments that reveal what doesn’t. If your team values thoughtful debates, small wins that compound, and closing the loop with users, we’ll get along. Happy to swap notes, review a problem statement, or jam on a discovery plan—reach out, and let’s build something useful.'
                }
            )}
            </p>
        </>
      ),
      incorrect: (
        <p>
          {intl.formatMessage(
            {
              id: 'infoPresentation.length.summaryIncorrect',
              defaultMessage:
                'I build products that turn data into decisions. Over the last three years I’ve shipped customer-facing features across growth, onboarding, and analytics, partnering with design and engineering to move the right metrics. I enjoy simplifying complex problems and turning research into crisp roadmaps.'
            }
          )}
        </p>
      ),
    },
    Experience: {
      text: (
       <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText1',
                    defaultMessage:
                        'Each role needs enough space to show context, actions, and results without drifting into a wall of text. Right-sized entries improve skimmability, help recruiters compare roles quickly, and surface the proof behind your title. Write within 800–1,500 characters to hit the sweet spot. '
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText3',
                    defaultMessage:
                        'Opens with role scope and team or product context'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText4',
                    defaultMessage:
                        '3–6 concise bullets or short lines focused on actions and results'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText5',
                    defaultMessage:
                        'At least two quantified outcomes where possible'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceText6',
                    defaultMessage:
                        'Keywords woven in naturally, not stuffed'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect1',
                    defaultMessage:
                        'Led onboarding revamp across web and mobile, raising activation by 14 percent within two quarters.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect2',
                    defaultMessage:
                        'Partnered with design and data to ship experiment tracking that cut iteration time from weeks to days.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect3',
                    defaultMessage:
                        'Prioritized a self-serve analytics view used weekly by leadership to monitor funnel health.'
                }, { isUKEnable: isUKEnable ? 'yes' : 'no' })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect4',
                    defaultMessage:
                        'Built discovery cadence with ten user conversations per month, informing roadmap and pricing tests.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect5',
                    defaultMessage:
                        'Why this works'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceCorrect6',
                    defaultMessage:
                        'Four tight bullets show scope, actions, and measurable results within the optimal range.'
                })}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect1',
                    defaultMessage:
                        'Managed features for growth.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect2',
                    defaultMessage:
                        'Worked with teams to ship updates.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect3',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect4',
                    defaultMessage:
                        'Not enough context or outcomes to establish credibility.'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect5',
                    defaultMessage:
                        'I owned multiple growth initiatives across channels and collaborated with many cross-functional stakeholders to deliver a variety of improvements. Responsibilities included writing PRDs and coordinating sprints.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect6',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect7',
                    defaultMessage:
                        'Break into concise bullets, surface outcomes, and cut process detail that doesn’t prove impact.'
                })}
            </p>
        </>
      ),
    },
    Headline: {
      text: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText1',
                    defaultMessage:
                        'Your headline should quickly tell who you are and why it matters. Aim for a compact, keyword-rich line that reads naturally on mobile and desktop. Right-sized headlines rank better in search, set context before a recruiter opens your profile, and reduce bounce by signaling relevance.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText3',
                    defaultMessage:
                        'Leads with role or area of impact'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText4',
                    defaultMessage:
                        'States value or audience serveds'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText5',
                    defaultMessage:
                        'Includes 2–4 natural keywords'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.headlineText6',
                    defaultMessage:
                        'Reads smoothly, no buzzword stuffing'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.length.headlineCorrect1',
                    defaultMessage:
                        'Product Manager, AI SaaS — I turn customer data into product growth. B2C/B2B, activation, onboarding, experimentation.'
                    },
                )}
            </p>
            <br />
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.length.headlineCorrect2',
                    defaultMessage:
                        'Data Analyst — SQL, Python, dashboards. I translate messy data into decisions for growth and operations teams.'
                    },
                )}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.length.headlineIncorrect1',
                    defaultMessage:
                        'Product enthusiast. Hardworking. Seeking opportunities.'
                    },
                )}
            </p>
            <br />
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.length.headlineIncorrect2',
                    defaultMessage:
                        'Data ninja. Numbers guy.'
                    },
                )}
            </p>
        </>
      ),
    },
    Education: {
      text: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText1',
                    defaultMessage:
                        'Each entry should cover degree, focus, institution, {isUKEnable, select, true {honours} other {honors}}, and one standout highlight. Keep it concise and scannable. Right-sized entries help recruiters confirm qualifications quickly and leave room for experience to do the heavy lifting. Write within 300-600 characters to hit the sweet spot.'
                }, { isUKEnable: isUKEnable ? 'true' : 'false' })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText3',
                    defaultMessage:
                        'Degree and field are explicit'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText4',
                    defaultMessage:
                        'Institution and graduation year are included'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText5',
                    defaultMessage:
                        'One honor, project, or leadership role'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText6',
                    defaultMessage:
                        'Optional coursework only if directly relevant'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationText7',
                    defaultMessage:
                        'No duplicate details already covered elsewhere'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect1',
                    defaultMessage:
                        'Header'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect2',
                    defaultMessage:
                        'B.Sc., Computer Science — Riverton University, 2024'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect3',
                    defaultMessage:
                        'Highlights'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect4',
                    defaultMessage:
                        'Dean’s List, two semesters'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect5',
                    defaultMessage:
                        'Capstone on recommendation systems adopted by campus library'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect6',
                    defaultMessage:
                        'Teaching assistant, Data Structures'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect7',
                    defaultMessage:
                        'Why this works'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationCorrect8',
                    defaultMessage:
                        'Degree, institution, year, one honor, and meaningful highlights within the optimal range.'
                })}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.experienceIncorrect1',
                    defaultMessage:
                        'BEng, Mechanical Engineering at Westlake Institute, completed numerous courses including Thermodynamics, Materials, Control Systems, Manufacturing Processes, Design, CAD, Robotics, Dynamics, and various labs, participated in multiple clubs, volunteered at several events, attended many workshops and seminars across semesters.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationIncorrect2',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.length.educationIncorrect3',
                    defaultMessage:
                        'Trim to essentials. Keep degree, institution, year, and one standout honor or project. Remove course lists and filler.'
                })}
            </p>
        </>
      ),
    },
  },
  structure: {
    Summary: {
      text: (
        <>
            <p style={{ whiteSpace: 'pre-line' }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText1',
                    defaultMessage:
                        'Use three short paragraphs, each two to four sentences. Keep sentences tight and in the first person. Recruiters skim. Clear paragraphing surfaces your value in seconds and increases replies and profile dwell time.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText3',
                    defaultMessage:
                        'Three short paragraphs, not bullets'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText4',
                    defaultMessage:
                        'First sentence of each paragraph sets the topic'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText5',
                    defaultMessage:
                        'Outcomes placed in the second paragraph'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText6',
                    defaultMessage:
                        'First-person voice throughout'
                })}
            </p>
            <p style={{whiteSpace: 'pre-line'}}>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.summaryText7',
                    defaultMessage:
                        'Line breaks every 2–4 sentences'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect1',
                    defaultMessage:
                        'Paragraph 1 (Hook)'
                    },
                )}
            </p>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect2',
                    defaultMessage:
                        'I build data-informed products that turn user insight into growth. I enjoy simplifying complex problems and shipping outcomes.'
                    },
                )}
            </p>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect3',
                    defaultMessage:
                        'Paragraph 2 (Impact)'
                    },
                )}
            </p>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect4',
                    defaultMessage:
                        'Recently, I reduced churn by focusing on activation moments, launched experiment tracking for faster learning, and shipped an analytics view used by leadership weekly.'
                    },
                )}
            </p>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect5',
                    defaultMessage:
                        'Paragraph 3 (Fit + CTA)'
                    },
                )}
            </p>
            <p>
                {intl.formatMessage(
                    {
                    id: 'infoPresentation.structure.summaryCorrect6',
                    defaultMessage:
                        'I’m excited about AI-powered tools in consumer tech and education. Happy to swap ideas or collaborate on product challenges.'
                    },
                )}
            </p>
        </>
      ),
      incorrect: (
        <p>
          {intl.formatMessage(
            {
              id: 'infoPresentation.structure.summaryIncorrect',
              defaultMessage:
                'I am a product enthusiast passionate about innovation, collaboration, and teamwork and I have worked on many projects where we achieved good results and learned a lot from customers and stakeholders and this helped the company and I am looking for new opportunities.'
            },
          )}
        </p>
      ),
    },
    Experience: {
      text: (
       <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText1',
                    defaultMessage:
                        'Use a tight, repeatable pattern for every role. Lead with scope, then show actions and results in short bullets. Recruiters skim. A consistent pattern highlights what changed because of you and speeds comparison across roles.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText3',
                    defaultMessage:
                        'Opener states scope in one line'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText4',
                    defaultMessage:
                        'Bullets begin with a strong verb'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText5',
                    defaultMessage:
                        'Results and metrics placed at the end'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText6',
                    defaultMessage:
                        'Similar length and grammar across bullets'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceText7',
                    defaultMessage:
                        'Tools named only when tied to outcomes'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect1',
                    defaultMessage:
                        'Redesigned first-run flow and lifted day-seven activation by 14 percent within two quarters.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect2',
                    defaultMessage:
                        'Introduced experiment cadence and cut iteration time from weeks to days.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect3',
                    defaultMessage:
                        'Launched self-serve funnel view used weekly by leadership to prioritize work.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect4',
                    defaultMessage:
                        'Built monthly discovery rhythm with ten user conversations to inform roadmap.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect5',
                    defaultMessage:
                        'Why this works'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceCorrect6',
                    defaultMessage:
                        'Clear opener, four focused bullets, verbs first, measurable results at the end.'
                })}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceIncorrect1',
                    defaultMessage:
                        'Worked on many features with the team and handled PRDs, sprint ceremonies, reporting, and documentation. Responsible for growth initiatives and cross-functional collaboration across several quarters.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceIncorrect2',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceIncorrect3',
                    defaultMessage:
                        'Add a one-line scope opener, convert to short bullets, lead with actions, and end each line with a result.'
                })}
            </p>
        </>
      ),
    },
    Education: {
      text: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText1',
                    defaultMessage:
                        'Use a simple header plus a few short highlights. Keep it consistent across entries. A predictable layout makes it easy to verify qualifications, notice standout achievements, and move quickly to your experience.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText3',
                    defaultMessage:
                        'Header includes degree, field, institution, and year'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText4',
                    defaultMessage:
                        'Two to four short lines, each about one highlight'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText5',
                    defaultMessage:
                        '{isUKEnable, select, true {Honours} other {Honors}} or awards if notable'
                }, { isUKEnable: isUKEnable ? 'true' : 'false' })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText6',
                    defaultMessage:
                        'One project or research item with a clear outcome'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText7',
                    defaultMessage:
                        'Leadership or activities only if they show impact'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationText8',
                    defaultMessage:
                        'Course names only when directly relevant'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect1',
                    defaultMessage:
                        'Header'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect2',
                    defaultMessage:
                        'B.Sc., Computer Science — Riverton University, 2024'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect3',
                    defaultMessage:
                        'Highlights'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect4',
                    defaultMessage:
                        'Dean’s List, two semesters'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect5',
                    defaultMessage:
                        'Capstone on recommendation systems adopted by campus library'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect6',
                    defaultMessage:
                        'Teaching assistant, Data Structures'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect7',
                    defaultMessage:
                        'Why this works'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationCorrect8',
                    defaultMessage:
                        'Clear header and three focused lines that signal credibility and impact.'
                })}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.experienceIncorrect1',
                    defaultMessage:
                        'Completed a Bachelor in Computer Science at Riverton University where I took many courses including algorithms, databases, operating systems, and networks, participated in several clubs and events, volunteered at different university activities, and attended workshops across semesters.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationIncorrect2',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.structure.educationIncorrect3',
                    defaultMessage:
                        'Replace the paragraph with a clear heading and two to four short lines. Keep only {isUKEnable, select, true {honours} other {honors}}, one project, and one role that show impact.'
                }, { isUKEnable: isUKEnable ? 'true' : 'false' })}
            </p>
        </>
      ),
    },
  },
  grammar: {
    Summary: {
      text: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText1',
                    defaultMessage:
                        'Grammar covers punctuation, subject–verb agreement, tense and aspect, capitalization, spelling, and consistent first-person voice. Clear grammar signals care and credibility. It reduces friction for fast skimming and helps your achievements stand out.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText3',
                    defaultMessage:
                        'Use first person consistently'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText4',
                    defaultMessage:
                        'Keep present tense for your current role and past tense for previous roles'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText5',
                    defaultMessage:
                        'Ensure subject–verb agreement in every sentence'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText6',
                    defaultMessage:
                        'Use clean punctuation and short sentences'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText7',
                    defaultMessage:
                        'Capitalize proper nouns, degrees, products, and organizations'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText8',
                    defaultMessage:
                        'Keep spelling consistent, including US or UK variants'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.summaryText9',
                    defaultMessage:
                        'Prefer clear words over jargon'
                })}
            </p>
        </>
      ),
      correct: (
        <p>
          {intl.formatMessage(
            {
              id: 'infoPresentation.grammar.summaryCorrect',
              defaultMessage:
                'I design data-informed products that make decisions easier. Recently I reduced churn by improving activation and launched an experiment framework that sped up learning. I am focused on AI tools in consumer tech and education, and I enjoy testing ideas with users.'
            },
          )}
        </p>
      ),
      incorrect: (
        <p>
          {intl.formatMessage(
            {
              id: 'infoPresentation.grammar.summaryIncorrect',
              defaultMessage:
                'Nancy is designing products and have reduced churn. launched an experiment framework which improved learnings, and we’re passionate about AI in EdTech and Consumer. looking for opportunities.'
            },
          )}
        </p>
      ),
    },
    Experience: {
      text: (
        <>
            <p>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText1',
                    defaultMessage:
                        'Strong grammar makes your results effortless to scan and trust. Grammar covers punctuation, subject–verb agreement, tense and sequence, capitalization, spelling, and consistent bullet style. Bullets can be first-person implied without “I,” or use a one-line opener in first person. Pick one approach and stay consistent.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText2',
                    defaultMessage:
                        'Quick checklist',
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText3',
                    defaultMessage:
                        'Use present tense for your current role and past tense for previous roles'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText4',
                    defaultMessage:
                        'Keep subject–verb agreement tight in every bullet'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText5',
                    defaultMessage:
                        'Start bullets with strong verbs and keep a parallel form across lines'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText6',
                    defaultMessage:
                        'End bullets consistently with or without periods'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText7',
                    defaultMessage:
                        'Capitalize proper nouns, product names, teams, and technologies correctly'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText8',
                    defaultMessage:
                        'Keep spelling consistent, including US or UK variants'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceText9',
                    defaultMessage:
                        'Use numerals for metrics and time frames, and keep percent formatting consistent'
                })}
            </p>
        </>
      ),
      correct: (
        <>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect1',
                    defaultMessage:
                        'Redesign first-run flow and lift day-seven activation by 14 percent in two quarters.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect2',
                    defaultMessage:
                        'Introduce weekly experiment cadence and cut iteration time from weeks to days.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect3',
                    defaultMessage:
                        'Launch self-serve funnel view used by leadership to prioritize work.'
                })}
            </p>
            <p>
                <span>{String.fromCharCode(9642)} </span>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect4',
                    defaultMessage:
                        'Interview ten users monthly and translate insights into roadmap decisions.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect5',
                    defaultMessage:
                        'Why this works'
                })}
            </p>
            <p style={{ whiteSpace: 'pre-line'}}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceCorrect6',
                    defaultMessage:
                        'Parallel verbs, clean punctuation, consistent tense, correct capitalization, and numerals for metrics.'
                })}
            </p>
        </>
      ),
      incorrect: (
        <>
            <p style={{ whiteSpace: 'pre-line' }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceIncorrect1',
                    defaultMessage:
                        'Worked on onboarding flows and experiments and is responsible for analytics dashboards and talking to users. launched many tests which was successful and reduced churn. Some bullets end without punctuation while others do. Passionate about teamwork and innovation.'
                })}
            </p>
            <br />
            <p style={{ fontWeight: 500 }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceIncorrect2',
                    defaultMessage:
                        'What to fix'
                })}
            </p>
            <p style={{ whiteSpace: 'pre-line' }}>
                {intl.formatMessage({
                    id: 'infoPresentation.grammar.experienceIncorrect3',
                    defaultMessage:
                        'Align tense, fix agreement, capitalize sentence starts and product names, choose one bullet-ending style, and split into focused lines with a single idea each.'
                })}
            </p>
        </>
      ),
    },
  },
})

class InfoPresentation extends Component {
  handleTab(id, tabId) {
    $('.as-info-modal-wrapper')
      .find('li')
      .removeClass('active')
      .attr('aria-selected', false)
    $('.as-info-modal-wrapper').find('div').removeClass('show active')
    $(tabId).addClass('active').attr('aria-selected', true)
    $(id).addClass('show active')
  }
  // Formatted text JSON map:

  render() {
    const { currentSection, isUKEnable, intl } = this.props
    let sectionData = sectionDataFunc(isUKEnable, intl)
    return (
      <div>
        <div className="as-info-modal-wrapper">
          <div className="as-info-modal-close">
            <a
              href="javascript:void(0);"
              aria-label={intl.formatMessage({
                id: 'aspire.infoScreenAriaLabel.close',
                defaultMessage: 'Click to close the information modal',
            })}
              onClick={() => this.props.hideModal()}
              className="icon-cross as-profile-edit-close-btn modal-close-button"
            />
          </div>
          <div className="as-info-modal-heading">
            {intl.formatMessage({
              id: 'aspire.infoPresentation.heading',
              defaultMessage: 'Presentation',
            })}
          </div>
          <div className="as-info-modal-subheading">
            {intl.formatMessage({
              id: 'aspire.infoPresentation.subheading',
              defaultMessage: 'What is Presentation?',
            })}
          </div>
          <div className="as-info-modal-desc">
            {intl.formatMessage({
              id: 'aspire.infoPresentation.description',
              defaultMessage:
                'Presentation is how you clearly convey credibility, relevance and substance at a glance. A proper and well-sized section improves scannability, search visibility and click-through.'
            })}
            {' '}
          </div>
          <div className="as-info-modal-nav">
            <div className="as-info-modal-nav-div">
              <div>
                <ul 
                  className="nav nav-tabs as-info-modal-nav-tabs" 
                  role="tablist"
                  tabIndex={0}
                  style={{ display: 'flex' }}
                >
                  <li
                    role="presentation"
                    aria-selected={true}
                    className="active"
                    id="lengthTab">
                    <a
                      role="tab"
                      aria-label={intl.formatMessage({
                        id: 'aspire.infoPresentation.lengthTab.ariaLabel',
                        defaultMessage: 'what is the role of length?',
                      })}
                      aria-controls="length"
                      onClick={this.handleTab.bind(
                        this,
                        '#length',
                        '#lengthTab'
                      )}
                      data-bs-toggle="tab">
                      {intl.formatMessage({
                        id: 'aspire.infoPresentation.lengthTab',
                        defaultMessage: 'Length',
                      })}
                    </a>
                  </li>
                  {currentSection !== 'Headline' ? 
                    <li role="presentation" id="structureTab" aria-selected={false}>
                        <a
                        role="tab"
                        aria-label={intl.formatMessage({
                            id: 'aspire.infoPresentation.structureTab.ariaLabel',
                            defaultMessage: 'what is the role of structure?',
                        })}
                        aria-controls="structure"
                        onClick={this.handleTab.bind(
                            this,
                            '#structure',
                            '#structureTab'
                        )}
                        data-bs-toggle="tab">
                        {intl.formatMessage({
                            id: 'aspire.infoPresentation.structureTab',
                            defaultMessage: 'Structure',
                        })}
                        </a>
                    </li> : null}
                  {currentSection !== 'Headline' && currentSection !== 'Education' ?
                    <li role="presentation" aria-selected={true} id="grammarTab">
                        <a
                        role="tab"
                        aria-label={intl.formatMessage({
                            id: 'aspire.infoPresentation.grammarTab.ariaLabel',
                            defaultMessage: 'what is the role of grammar?',
                        })}
                        aria-controls="grammar"
                        onClick={this.handleTab.bind(
                            this,
                            '#grammar',
                            '#grammarTab'
                        )}
                        data-bs-toggle="tab">
                        {intl.formatMessage({
                            id: 'aspire.infoPresentation.grammarTab',
                            defaultMessage: 'Grammar',
                        })}
                        </a>
                    </li> : null}
                </ul>
                <div className="tab-content">
                  <div
                    role="tabpanel"
                    className="tab-pane fade show active"
                    id="length">
                    <div className="as-info-modal-nav-content">
                      <div 
                        className="as-info-modal-nav-content-desc"
                      >
                        {sectionData['length'][currentSection]['text']}
                      </div>
                      <h3
                        className="smp-heading"
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'aspire.infoPresentation.sampleHeadlines',
                          defaultMessage: 'Sample Headlines',
                        })}>
                          {' '}
                        {intl.formatMessage({
                          id: 'aspire.infoPresentation.sampleHeadlines',
                          defaultMessage: 'Sample Headlines',
                        })}
                      </h3>
                      <div className="row as-info-modal-nav-content-row">
                        <div className="col-sm-6 smp-bullets">
                          <div className="smp-bullets-sec">
                            
                            <div className="smp-icon smp-green">
                              <i className="fa fa-thumbs-up" aria-hidden="true" />
                            </div>
                            <div className="smp-data">
                              <div
                                className="smp-data-heading"
                                tabIndex={0}
                                role="heading"
                                aria-label={intl.formatMessage({
                                    id: 'aspire.infoPresentation.correctWay',
                                    defaultMessage: 'Correct way',
                                })}>
                                {intl.formatMessage({
                                  id: 'aspire.infoPresentation.correctWay',
                                  defaultMessage: 'Correct way',
                                })}
                              </div>
                              {
                                sectionData['length'][currentSection][
                                  'correct'
                                ]
                              }
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-6 smp-bullets">
                          <div className="smp-bullets-sec">
                            <div className="smp-icon smp-red">
                              <i
                                className="fa fa-thumbs-down"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="smp-data">
                              <div
                                className="smp-data-heading"
                                tabIndex={0}
                                role="heading"
                                aria-label={intl.formatMessage({
                                    id: 'aspire.infoPresentation.incorrectWay',
                                    defaultMessage: 'Incorrect way',
                                })}>
                                {intl.formatMessage({
                                  id: 'aspire.infoPresentation.incorrectWay',
                                  defaultMessage: 'Incorrect way',
                                })}
                              </div>
                              {
                                sectionData['length'][currentSection][
                                  'incorrect'
                                ]
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {currentSection !== 'Headline' ?
                    <div role="tabpanel" className="tab-pane fade" id="structure">
                        <div className="as-info-modal-nav-content">
                        <div 
                            className="as-info-modal-nav-content-desc"
                        >
                            {sectionData['structure'][currentSection]['text']}
                        </div>
                        <h3
                            className="smp-heading"
                            tabIndex={0}
                            aria-label={intl.formatMessage({
                            id: 'aspire.infoPresentation.sampleBulletsHeading',
                            defaultMessage: 'Sample Bullets',
                            })}>
                            {' '}
                            {intl.formatMessage({
                            id: 'aspire.infoPresentation.sampleBulletsHeading',
                            defaultMessage: 'Sample Bullets',
                            })}
                        </h3>
                        <div className="row as-info-modal-nav-content-row">
                            <div className="col-sm-6 smp-bullets">
                            <div className="smp-bullets-sec">
                                <div className="smp-icon smp-green">
                                <i className="fa fa-thumbs-up" aria-hidden="true" />
                                </div>
                                <div className="smp-data">
                                <div
                                    className="smp-data-heading"
                                    tabIndex={0}
                                    role="heading"
                                    aria-label={intl.formatMessage({
                                        id: 'aspire.infoPresentation.correctWay',
                                        defaultMessage: 'Correct way',
                                    })}>
                                    {intl.formatMessage({
                                    id: 'aspire.infoPresentation.correctWay',
                                    defaultMessage: 'Correct way',
                                    })}
                                </div>
                                {sectionData['structure'][currentSection]['correct']}
                                </div>
                            </div>
                            </div>
                            <div className="col-sm-6 smp-bullets">
                            <div className="smp-bullets-sec">
                                <div className="smp-icon smp-red">
                                <i
                                    className="fa fa-thumbs-down"
                                    aria-hidden="true"
                                />
                                </div>
                                <div className="smp-data">
                                <div
                                    className="smp-data-heading"
                                    tabIndex={0}
                                    role="heading"
                                    aria-label={intl.formatMessage({
                                    id: 'aspire.infoPresentation.incorrectWay',
                                    defaultMessage: 'Incorrect way',
                                    })}>
                                    {intl.formatMessage({
                                    id: 'aspire.infoPresentation.incorrectWay',
                                    defaultMessage: 'Incorrect way',
                                    })}
                                </div>
                                {sectionData['structure'][currentSection]['incorrect']}
                                </div>
                            </div>
                            </div>
                        </div>
                        </div>
                    </div> : null}
                  {currentSection !== 'Headline' && currentSection !== 'Education' ?
                    <div role="tabpanel" className="tab-pane fade" id="grammar">
                        <div className="as-info-modal-nav-content">
                        <div 
                            className="as-info-modal-nav-content-desc"
                        >
                            {sectionData['grammar'][currentSection]['text']}
                        </div>
                        <h3
                            className="smp-heading"
                            tabIndex={0}
                            aria-label={intl.formatMessage({
                            id: 'aspire.infoPresentation.sampleBulletsHeading',
                            defaultMessage: 'Sample Bullets',
                            })}>
                            {' '}
                            {intl.formatMessage({
                            id: 'aspire.infoPresentation.sampleBulletsHeading',
                            defaultMessage: 'Sample Bullets',
                            })}
                        </h3>
                        <div className="row as-info-modal-nav-content-row">
                            <div className="col-sm-6 smp-bullets">
                            <div className="smp-bullets-sec">
                                <div className="smp-icon smp-green">
                                <i className="fa fa-thumbs-up" aria-hidden="true" />
                                </div>
                                <div className="smp-data">
                                <div
                                    className="smp-data-heading"
                                    tabIndex={0}
                                    role="heading"
                                    aria-label={intl.formatMessage({
                                    id: 'aspire.infoPresentation.correctWay',
                                    defaultMessage: 'Correct way',
                                    })}>
                                    {intl.formatMessage({
                                    id: 'aspire.infoPresentation.correctWay',
                                    defaultMessage: 'Correct way',
                                    })}
                                </div>
                                {sectionData['grammar'][currentSection]['correct']}
                                </div>
                            </div>
                            </div>
                            <div className="col-sm-6 smp-bullets">
                            <div className="smp-bullets-sec">
                                <div className="smp-icon smp-red">
                                <i
                                    className="fa fa-thumbs-down"
                                    aria-hidden="true"
                                />
                                </div>
                                <div className="smp-data">
                                <div
                                    className="smp-data-heading"
                                    tabIndex={0}
                                    role="heading"
                                    aria-label={intl.formatMessage({
                                    id: 'aspire.infoPresentation.incorrectWay',
                                    defaultMessage: 'Incorrect way',
                                    })}>
                                    {intl.formatMessage({
                                    id: 'aspire.infoPresentation.incorrectWay',
                                    defaultMessage: 'Incorrect way',
                                    })}
                                </div>
                                {sectionData['grammar'][currentSection]['incorrect']}
                                </div>

                            </div>
                            </div>
                        </div>
                        </div>
                    </div> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default injectIntl(InfoPresentation)